import { useState, useEffect, useCallback } from "react";
import { getLocale, useLanguage } from "../lang/useLanguage";
import { useTheme } from "../context/ThemeContext";
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}` });

const WIDGET_DEFS = {
    oee_gauges:       { titleKey: "oeeGauges", w: 2, h: 1 },
    production_chart: { titleKey: "productionChart", w: 2, h: 1 },
    alarm_list:       { titleKey: "alarmList", w: 1, h: 1 },
    energy:           { titleKey: "energy", w: 1, h: 1 },
    stock_critical:   { titleKey: "criticalStock", w: 1, h: 1 },
    kaizen_summary:   { titleKey: "kaizenSummary", w: 1, h: 1 },
    machine_map:      { titleKey: "machineMap", w: 2, h: 1 },
    loss_summary:     { titleKey: "lossSummary", w: 2, h: 1 },
    mrp_cost:         { titleKey: "mrpCost", w: 1, h: 1 },
};

const DEFAULT_LAYOUT = [
  "oee_gauges", "production_chart",
  "machine_map",
  "alarm_list", "energy", "stock_critical", "kaizen_summary",
  "loss_summary",
];

// ─── İÇERİK YETKİ TANIMLAMALARI ─────────────────────────────────────────────
// Her modülün içinde hangi öğeler açılıp kapatılabilir
const MODULE_ITEMS = {
  dashboard: {
    label: true,
    items: {
      stat_oee:           true,
      stat_production:    true,
      stat_alarms:        true,
      stat_loss:          true,
      widget_pl:          true,
      widget_machines:    true,
      widget_alarms:      true,
      widget_oee_gauges:  true,
      widget_production:  true,
      widget_energy:      true,
      widget_stock:       true,
      widget_kaizen:      true,
      widget_loss:        true,

      sales_month_amount:       "💰 Son 30 Gün Satış Tutarı",
      sales_order_count:        "🛒 Son 30 Gün Sipariş Sayısı",
      sales_pending_orders:     "⏳ Bekleyen Siparişler",
      sales_waiting_shipment:   "📦 Sevkiyat Bekleyenler",
      sales_completed_orders:   "✅ Tamamlanan Siparişler",
      sales_invoice_pending:    "🧾 Fatura Bekleyenler",
      sales_customer_count:     "👥 Müşteri Sayısı",
      sales_cancelled_orders:   "❌ İptal Siparişler",
      sales_recent_orders:      "📋 Son Satış Siparişleri",
      sales_top_products:       "🏆 En Çok Satan Ürünler",
    }
  },
  reports: {
    label: true,
    items: {
      tab_dashboard:  true,
      tab_fire:       true,
      tab_pl:         true,
      tab_sales_perf: true,
      tab_personnel:  true,
      tab_reports:    true,
      tab_history:    true,
      tab_variance:   true,
    }
  },
  salesorders: {
    label: true,
    items: {
      btn_new_order:    true,
      btn_approve:      true,
      btn_cancel:       true,
      col_financial:    true,
      tab_items:        true,
      tab_shipment:     true,
    }
  },
  machines: {
    label: true,
    items: {
      btn_add_machine:  true,
      col_oee:          true,
      col_temperature:  true,
      fault_history:    true,
    }
  },
  workorders: {
    label: true,
    items: {
      btn_new_order:       true,
      btn_export_excel:    true,
      btn_complete:        true,
      btn_runtime_start:   true,
      btn_runtime_check:   true,
      btn_downtime_reason: true,

      col_approval:        true,
      col_cost:            true,
      col_machine:         true,
      col_operator:        true,

      tab_production:      true,
      tab_materials:       true,
      tab_cost:            true,
      tab_revisions:       true,
      tab_audit:           true,

      action_tree:         true,
      action_detail:       true,
    }
  },
  stock: {
    label: true,
    items: {
      btn_add_stock:    true,
      btn_transaction:  true,
      col_price:        true,
      col_supplier:     true,
      tab_movements:    true,
    }
  },
  hr: {
    label: true,
    items: {
      btn_add_employee: true,
      col_salary:       true,
      col_attendance:   true,
      tab_payroll:      true,
      tab_leave:        true,
    }
  },
};

const ROLES = [
  "admin", "manager", "production_manager", "quality_manager",
  "warehouse_chief", "maintenance_chief", "hr_manager", "accountant",
  "technician", "warehouse", "operator", "purchasing",
  "dis_ticaret_mudur", "dis_ticaret_uzman", "dis_ticaret_temsilci",
  "ic_satis_mudur", "ic_satis_uzman", "ic_satis_temsilci",
];


// ─── İÇERİK YETKİ PANELİ ─────────────────────────────────────────────────
const ContentPermissionsPanel = ({ t, ap }) => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedRole, setSelectedRole] = useState("production_manager");
  const [selectedModule, setSelectedModule] = useState("dashboard");

  useEffect(() => {
    fetch(`${API}/content-permissions/all`, { headers: headers() })
      .then(r => r.ok ? r.json() : {})
      .then(d => { setPermissions(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = async (role, module, itemKey) => {
    const current = permissions[role]?.[module]?.[itemKey];
    const newVal = current === false ? true : false; // default true, toggle to false

    setPermissions(prev => ({
      ...prev,
      [role]: {
        ...(prev[role] || {}),
        [module]: {
          ...(prev[role]?.[module] || {}),
          [itemKey]: newVal,
        }
      }
    }));

    setSaving(true);
    try {
      await fetch(`${API}/content-permissions/${role}/${module}/${itemKey}`, {
        method: "PATCH",
        headers: { ...headers(), "Content-Type": "application/json" },
        body: JSON.stringify({ is_visible: newVal }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  const toggleAll = async (role, module, value) => {
    const moduleItems = MODULE_ITEMS[module]?.items || {};
    const updates = {};
    Object.keys(moduleItems).forEach(k => { updates[k] = value; });

    setPermissions(prev => ({
      ...prev,
      [role]: { ...(prev[role] || {}), [module]: updates }
    }));

    setSaving(true);
    try {
      await Promise.all(Object.keys(moduleItems).map(k =>
        fetch(`${API}/content-permissions/${role}/${module}/${k}`, {
          method: "PATCH",
          headers: { ...headers(), "Content-Type": "application/json" },
          body: JSON.stringify({ is_visible: value }),
        })
      ));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {}
    setSaving(false);
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.text.muted }}>{ap.loading}</div>;

  const rolePerms = permissions[selectedRole] || {};
  const modulePerms = rolePerms[selectedModule] || {};
  const moduleItems = MODULE_ITEMS[selectedModule]?.items || {};

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: t.text.primary, margin: 0 }}>{ap.permissionsTitle}</h2>
          <p style={{ fontSize: 13, color: t.text.muted, margin: "4px 0 0" }}>
            Rol → Modül → İçerik bazında erişim kontrolü
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {saved && <span style={{ fontSize: 12, color: t.accent.green, fontWeight: 600 }}>✓ {ap.saved}</span>}
          {saving && <span style={{ fontSize: 12, color: t.accent.yellow }}>{ap.saving}</span>}
        </div>
      </div>

      {/* ADIM 1: Rol Seç */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          1. Rol Seçin
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {ROLES.map(role => (
            <button key={role} onClick={() => setSelectedRole(role)} style={{
              padding: "6px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
              border: `1px solid ${selectedRole === role ? t.accent.blue : t.border.input}`,
              background: selectedRole === role ? t.accent.blueBg : t.bg.toggle,
              color: selectedRole === role ? t.accent.blue : t.text.muted,
              transition: "all 0.15s",
            }}>{ap.roles[role] || role}</button>
          ))}
        </div>
      </div>

      {/* ADIM 2: Modül Seç */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
          2. Modül Seçin
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {Object.entries(MODULE_ITEMS).map(([modKey, modDef]) => (
            <button key={modKey} onClick={() => setSelectedModule(modKey)} style={{
              padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600,
              border: `2px solid ${selectedModule === modKey ? t.accent.blue : t.border.input}`,
              background: selectedModule === modKey ? t.accent.blueBg : t.bg.toggle,
              color: selectedModule === modKey ? t.accent.blue : t.text.muted,
              transition: "all 0.15s",
            }}>{ap.permissionModules[modKey] || modKey}</button>
          ))}
        </div>
      </div>

      {/* ADIM 3: İçerik Toggle */}
      <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `1px solid ${t.border.default}`,
          display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text.primary }}>
              {ap.roles[selectedRole] || selectedRole} → {ap.permissionModules[selectedModule] || selectedModule}
            </div>
            <div style={{ fontSize: 11, color: t.text.muted, marginTop: 2 }}>
              {Object.values(modulePerms).filter(v => v !== false).length + Object.keys(moduleItems).filter(k => modulePerms[k] === undefined).length} / {Object.keys(moduleItems).length} öğe görünür
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => toggleAll(selectedRole, selectedModule, true)}
              style={{ padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                border: `1px solid ${t.accent.green}44`, background: t.accent.greenBg, color: t.accent.green }}>
              Tümünü Aç
            </button>
            <button onClick={() => toggleAll(selectedRole, selectedModule, false)}
              style={{ padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red }}>
              Tümünü Kapat
            </button>
          </div>
        </div>

        {Object.entries(moduleItems).map(([itemKey, itemLabel], i) => {
          const isVisible = modulePerms[itemKey] !== false;
          return (
            <div key={itemKey} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "14px 20px",
              borderBottom: i < Object.keys(moduleItems).length - 1 ? `1px solid ${t.border.subtle}` : "none",
              background: isVisible ? "transparent" : `${t.accent.red}08`,
              transition: "background 0.2s",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: isVisible ? t.text.primary : t.text.muted }}>
                  {ap.permissionItems[itemKey] || itemKey}
                </div>
                <div style={{ fontSize: 10, color: t.text.muted, marginTop: 2, fontFamily: "monospace" }}>{itemKey}</div>
              </div>
              <div onClick={() => toggle(selectedRole, selectedModule, itemKey)} style={{
                width: 44, height: 24, borderRadius: 12, cursor: "pointer",
                background: isVisible ? t.accent.blue : t.bg.tag,
                border: `1px solid ${isVisible ? t.accent.blue : t.border.input}`,
                position: "relative", transition: "all 0.2s", flexShrink: 0,
              }}>
                <div style={{
                  position: "absolute", top: 2, left: isVisible ? 22 : 2,
                  width: 18, height: 18, borderRadius: "50%",
                  background: "#fff", transition: "left 0.2s",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                }} />
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: "10px 16px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 12, color: t.accent.blue }}>
        ℹ️ Değişiklikler anında DB'ye kaydedilir. Kullanıcı sayfayı yenileyince geçerli olur.
      </div>
    </div>
  );
};

const MiniGauge = ({ value, label, size = 56, t }) => {
  const pct = Math.min(100, Math.max(0, value || 0));
  const color = pct >= 85 ? "#00ff9d" : pct >= 70 ? "#fbbf24" : "#f87171";
  const r = (size / 2) - 5;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <div style={{ position: "relative", width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={t.bg.tag} strokeWidth={5} />
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round" />
        </svg>
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: size * 0.18, fontWeight: 800, color, lineHeight: 1 }}>{pct.toFixed(0)}%</span>
        </div>
      </div>
      <span style={{ fontSize: 10, color: t.text.muted, textAlign: "center", maxWidth: size + 10 }}>{label}</span>
    </div>
  );
};

const OEEGaugesWidget = ({ data, t, ap }) => {
  const machines = data?.machines || [];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", alignItems: "center", height: "100%", padding: "8px 0" }}>
      {machines.length === 0 ? <span style={{ color: t.text.placeholder, fontSize: 13 }}>{ap.widgets.dataLoading}</span>
        : machines.map((m, i) => <MiniGauge key={i} value={m.oee > 1 ? m.oee : m.oee * 100} label={m.machine} size={64} t={t} />)}
    </div>
  );
};

const ProductionChartWidget = ({ data, t, ap }) => {
  const chart = data?.productionChart || [];
  return chart.length === 0 ? (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.text.placeholder, fontSize: 13 }}>{ap.widgets.noData}</div>
  ) : (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={chart} margin={{ top: 5, right: 5, bottom: 5, left: 0 }}>
        <defs>
          <linearGradient id="prodGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={t.accent.blue} stopOpacity={0.3} />
            <stop offset="95%" stopColor={t.accent.blue} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
        <XAxis dataKey="date" stroke={t.chart.axis} tick={{ fontSize: 9, fill: t.chart.axis }} tickFormatter={v => v?.slice(5)} />
        <YAxis stroke={t.chart.axis} tick={{ fontSize: 9, fill: t.chart.axis }} />
        <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 6, fontSize: 11 }} />
        <Area type="monotone" dataKey="production" stroke={t.accent.blue} fill="url(#prodGrad)" strokeWidth={2} name={ap.widgets.production} />
        <Area type="monotone" dataKey="oee" stroke={t.accent.green} fill="none" strokeWidth={2} strokeDasharray="4 2" name="OEE %" />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const AlarmListWidget = ({ data, t, ap }) => {
  const alarms = data?.alarms || [];
  const LEVEL_COLOR = { KRITIK: "#f87171", UYARI: "#fbbf24", NORMAL: "#00ff9d" };
  return (
    <div style={{ overflowY: "auto", height: "100%" }}>
      {alarms.length === 0 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.text.placeholder, fontSize: 13 }}>✅ {ap.widgets.noActiveAlarms}</div>
      ) : alarms.map((a, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: `1px solid ${t.border.subtle}` }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: LEVEL_COLOR[a.alarm_level] || t.text.muted }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>{a.machine}</div>
            <div style={{ fontSize: 10, color: t.text.muted }}>OEE: {a.oee_percent}% · {a.temperature}°C</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 20, flexShrink: 0, color: LEVEL_COLOR[a.alarm_level] || t.text.muted, background: `${LEVEL_COLOR[a.alarm_level] || t.text.muted}18` }}>{a.alarm_level}</span>
        </div>
      ))}
    </div>
  );
};

const EnergyWidget = ({ data, t, ap }) => {
  const e = data?.energy || {};
  const chart = data?.energyChart || [];
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flexShrink: 0 }}>
        {[{ label: ap.widgets.today, value: `${e.today_kwh || 0} kWh`, color: t.accent.blue }, { label: ap.widgets.cost, value: `${(e.today_cost || 0).toFixed(0)} ₺`, color: t.accent.red }].map(({ label, value, color }) => (
          <div key={label} style={{ background: t.bg.tag, borderRadius: 8, padding: "8px 10px", border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 14, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        {chart.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart.slice(-7)} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.chart.grid} />
              <XAxis dataKey="date" stroke={t.chart.axis} tick={{ fontSize: 8, fill: t.chart.axis }} tickFormatter={v => v?.slice(5)} />
              <YAxis stroke={t.chart.axis} tick={{ fontSize: 8, fill: t.chart.axis }} />
              <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 6, fontSize: 10 }} />
              <Bar dataKey="kwh" fill={t.accent.blue} radius={[3,3,0,0]} name="kWh" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.text.placeholder, fontSize: 12 }}>{ap.widgets.noChartData}</div>}
      </div>
    </div>
  );
};

const StockCriticalWidget = ({ data, t, ap }) => {
  const items = (data?.stock || []).filter(s => (s.current_stock || 0) <= (s.min_stock || 0));
  return (
    <div style={{ overflowY: "auto", height: "100%" }}>
      {items.length === 0 ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.text.placeholder, fontSize: 13 }}>✅ {ap.widgets.noCriticalStock}</div>
      ) : items.map((s, i) => {
        const pct = s.min_stock > 0 ? Math.min(100, (s.current_stock / s.min_stock) * 100) : 0;
        return (
          <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${t.border.subtle}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>{s.name}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: t.accent.red }}>{s.current_stock} / {s.min_stock} {s.unit}</span>
            </div>
            <div style={{ height: 4, background: t.bg.tag, borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: pct <= 50 ? t.accent.red : t.accent.yellow, borderRadius: 2 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KaizenSummaryWidget = ({ data, t, ap }) => {
  const k = data?.kaizenStats || {};
  const pieData = [
    { name: ap.widgets.completed, value: k.completed || 0, color: "#00ff9d" },
    { name: ap.widgets.inProgress, value: k.inProgress || 0, color: "#fbbf24" },
    { name: ap.widgets.pending, value: k.pending || 0, color: "#60a5fa" },
  ].filter(d => d.value > 0);
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, flexShrink: 0 }}>
        {[{ label: ap.widgets.total, value: k.total || 0, color: t.accent.blue }, { label: ap.widgets.saving, value: k.saving ? `${(k.saving/1000).toFixed(0)}K₺` : "0₺", color: t.accent.green }].map(({ label, value, color }) => (
          <div key={label} style={{ background: t.bg.tag, borderRadius: 8, padding: "8px 10px", border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color }}>{value}</div>
          </div>
        ))}
      </div>
      {pieData.length > 0 ? (
        <div style={{ flex: 1, minHeight: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius="70%" label={false}>
                {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: t.chart.tooltip.bg, border: `1px solid ${t.chart.tooltip.border}`, borderRadius: 6, fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: t.text.placeholder, fontSize: 12 }}>{ap.widgets.noKaizen}</div>}
    </div>
  );
};

const MachineMapWidget = ({ data, t, ap }) => {
  const machines = data?.machines || [];
  const STATUS_COLOR = { running: "#00ff9d", stopped: "#94a3b8", fault: "#f87171", maintenance: "#fbbf24", idle: "#60a5fa" };
  const STATUS_LABEL = {
    running: ap.widgets.statusRunning,
    stopped: ap.widgets.statusStopped,
    fault: ap.widgets.statusFault,
    maintenance: ap.widgets.statusMaintenance,
    idle: ap.widgets.statusIdle,
  };
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", flexShrink: 0 }}>
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: STATUS_COLOR[k] }} />
            <span style={{ color: t.text.muted }}>{v}</span>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 10, alignContent: "flex-start", overflowY: "auto" }}>
        {machines.length === 0 ? (
          <div style={{ width: "100%", textAlign: "center", color: t.text.placeholder, fontSize: 13, paddingTop: 20 }}>Veri yükleniyor...</div>
        ) : machines.map((m, i) => {
          const color = STATUS_COLOR[m.status] || STATUS_COLOR.stopped;
          const oee = m.oee > 1 ? m.oee : m.oee * 100;
          return (
            <div key={i} style={{ background: `${color}12`, border: `1.5px solid ${color}44`, borderRadius: 12, padding: "10px 14px", minWidth: 120, flex: "1 1 120px", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: color }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 4 }}>{m.machine}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11, color, fontWeight: 700 }}>{STATUS_LABEL[m.status] || m.status}</span>
                <span style={{ fontSize: 12, fontWeight: 800, color: oee >= 85 ? "#00ff9d" : oee >= 70 ? "#fbbf24" : "#f87171" }}>{oee.toFixed(1)}%</span>
              </div>
              <div style={{ marginTop: 6, fontSize: 10, color: t.text.muted }}>🌡 {parseFloat(m.temperature || 0).toFixed(1)}°C</div>
              <div style={{ marginTop: 6, height: 3, background: t.bg.tag, borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${oee}%`, background: oee >= 85 ? "#00ff9d" : oee >= 70 ? "#fbbf24" : "#f87171", borderRadius: 2 }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LossSummaryWidget = ({ data, t, ap }) => {
  const financial = data?.financial || [];
  const totalDaily = financial.reduce((s, f) => s + (parseFloat(f.daily_loss) || 0), 0);
  const totalMonthly = financial.reduce((s, f) => s + (parseFloat(f.monthly_loss) || 0), 0);
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, flexShrink: 0 }}>
        {[{ label: ap.widgets.dailyLoss, value: `${totalDaily.toLocaleString("tr-TR")} ₺` }, { label: ap.widgets.monthlyLoss, value: `${totalMonthly.toLocaleString("tr-TR")} ₺` }].map(({ label, value }) => (
          <div key={label} style={{ background: t.accent.redBg, border: `1px solid ${t.accent.red}33`, borderRadius: 10, padding: "10px 14px" }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: t.accent.red }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {financial.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: t.text.placeholder, fontSize: 12 }}>{ap.widgets.noFinancialData}</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{[ap.widgets.machine, "OEE %", ap.widgets.dailyLoss, ap.widgets.monthlyLoss].map(h => (
                <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontSize: 9, fontWeight: 700, color: t.text.label, textTransform: "uppercase", borderBottom: `1px solid ${t.border.subtle}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {financial.map((f, i) => {
                const oee = parseFloat(f.oee || 0) * 100;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.border.subtle}` }}>
                    <td style={{ padding: "5px 8px", fontSize: 11, fontWeight: 600, color: t.text.primary }}>{f.machine}</td>
                    <td style={{ padding: "5px 8px", fontSize: 11, fontWeight: 700, color: oee >= 85 ? t.accent.green : oee >= 70 ? t.accent.yellow : t.accent.red }}>{oee.toFixed(1)}%</td>
                    <td style={{ padding: "5px 8px", fontSize: 11, color: t.accent.red }}>{parseFloat(f.daily_loss || 0).toLocaleString("tr-TR")} ₺</td>
                    <td style={{ padding: "5px 8px", fontSize: 11, color: t.accent.red }}>{parseFloat(f.monthly_loss || 0).toLocaleString("tr-TR")} ₺</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const renderWidget = (id, data, t, themeName, ap) => {
  switch (id) {
    case "oee_gauges":       return <OEEGaugesWidget data={data} t={t} ap={ap} />;
    case "production_chart": return <ProductionChartWidget data={data} t={t} ap={ap} />;
    case "alarm_list":       return <AlarmListWidget data={data} t={t} ap={ap} />;
    case "energy":           return <EnergyWidget data={data} t={t} ap={ap} />;
    case "stock_critical":   return <StockCriticalWidget data={data} t={t} ap={ap} />;
    case "kaizen_summary":   return <KaizenSummaryWidget data={data} t={t} ap={ap} />;
    case "machine_map":      return <MachineMapWidget data={data} t={t} ap={ap} themeName={themeName} />;
    case "loss_summary":     return <LossSummaryWidget data={data} t={t} ap={ap} />;
    default:                 return null;
  }
};

const useDragDrop = (initialLayout) => {
  const [layout, setLayout] = useState(initialLayout);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const handleDragStart = (id) => setDragging(id);
  const handleDragOver = (e, id) => { e.preventDefault(); setDragOver(id); };
  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragging || dragging === targetId) { setDragging(null); setDragOver(null); return; }
    setLayout(prev => {
      const next = [...prev];
      const fromIdx = next.indexOf(dragging);
      const toIdx = next.indexOf(targetId);
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, dragging);
      return next;
    });
    setDragging(null); setDragOver(null);
  };
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };
  return { layout, setLayout, dragging, dragOver, handleDragStart, handleDragOver, handleDrop, handleDragEnd };
};

// ─── WIDGET YETKİ YÖNETİMİ ───────────────────────────────────────────────
export default function AdminPanel() {
  const { t: langT } = useLanguage();
  const ap = langT.adminPanel;

  const { t, themeName } = useTheme();
  const [activeTab, setActiveTab] = useState(localStorage.getItem("tab_adminpanel") || "panel");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [hiddenWidgets, setHiddenWidgets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("adminHidden")) || [];
    } catch {
      return [];
    }
  });
  const [lastUpdate, setLastUpdate] = useState(null);

  const savedLayout = (() => { try { return JSON.parse(localStorage.getItem("adminLayout")) || DEFAULT_LAYOUT; } catch { return DEFAULT_LAYOUT; } })();
  const { layout, setLayout, dragging, dragOver, handleDragStart, handleDragOver, handleDrop, handleDragEnd } = useDragDrop(savedLayout);

  const fetchData = useCallback(async () => {
    try {
      const [r1, r2, r3, r4, r5, r6, r7] = await Promise.all([
        fetch(`${API}/machines`, { headers: headers() }),
        fetch(`${API}/alarms`, { headers: headers() }),
        fetch(`${API}/energy/summary`, { headers: headers() }),
        fetch(`${API}/energy/daily?days=7`, { headers: headers() }),
        fetch(`${API}/stock`, { headers: headers() }),
        fetch(`${API}/kaizen`, { headers: headers() }),
        fetch(`${API}/financial`, { headers: headers() }),
      ]);
      const newData = {};
      if (r1.ok) { const d = await r1.json(); newData.machines = d.machines || []; }
      if (r2.ok) { const d = await r2.json(); newData.alarms = d.alarms || []; }
      if (r3.ok) newData.energy = await r3.json();
      if (r4.ok) newData.energyChart = await r4.json();
      if (r5.ok) { const d = await r5.json(); newData.stock = Array.isArray(d) ? d : []; }
      if (r6.ok) {
        const d = await r6.json();
        const list = Array.isArray(d) ? d : [];
        newData.kaizenStats = {
          total: list.length,
          completed: list.filter(k => k.status === "completed").length,
          inProgress: list.filter(k => k.status === "implementing").length,
          pending: list.filter(k => k.status === "idea" || k.status === "reviewing").length,
          saving: list.filter(k => k.status === "completed").reduce((s, k) => s + (k.estimated_saving || 0), 0),
        };
      }
      if (r7.ok) { const d = await r7.json(); newData.financial = d.financial_summary || []; }
      const prodRes = await fetch(`${API}/reports/production-chart?days=14`, { headers: headers() });
      if (prodRes.ok) newData.productionChart = await prodRes.json();
      setData(newData);
      setLastUpdate(new Date());
    } catch (e) { console.log(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const toggleWidget = (id) => {
    setHiddenWidgets(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      localStorage.setItem("adminHidden", JSON.stringify(next));
      return next;
    });
  };

  const saveLayout = () => { localStorage.setItem("adminLayout", JSON.stringify(layout)); setEditMode(false); };
  const resetLayout = () => { setLayout(DEFAULT_LAYOUT); setHiddenWidgets([]); localStorage.removeItem("adminLayout"); localStorage.removeItem("adminHidden"); };
  const visibleLayout = layout.filter(id => !hiddenWidgets.includes(id));

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} } @keyframes sf-pulse { 0%,100%{opacity:1} 50%{opacity:0.4} } .widget-card{transition:all 0.2s} .widget-card:hover{transform:translateY(-1px)} .widget-drag-over{outline:2px dashed #3b82f6 !important;outline-offset:2px}`}</style>

      <div style={{ maxWidth: 1400, margin: "0 auto", padding: "28px 24px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0 }}>{ap.title}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>
              {ap.subtitle}
              {lastUpdate && <span style={{ marginLeft: 8, color: t.accent.green }}>· {ap.lastUpdate}: {lastUpdate.toLocaleTimeString(getLocale())}</span>}
            </p>
          </div>
          {activeTab === "panel" && (
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={fetchData} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄</button>
              {editMode ? (
                <>
                  <button onClick={resetLayout} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.accent.red}44`, background: t.accent.redBg, color: t.accent.red, cursor: "pointer", fontSize: 13 }}>↺ {ap.reset}</button>
                  <button onClick={saveLayout} style={{ padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#059669,#10b981)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>✓ {ap.save}</button>
                </>
              ) : (
                <button onClick={() => setEditMode(true)} style={{ padding: "10px 20px", borderRadius: 10, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>✏️ {ap.edit}</button>
              )}
            </div>
          )}
        </div>

        {/* Sekmeler */}
        <div style={{ display: "flex", gap: 2, marginBottom: 24, borderBottom: `1px solid ${t.border.divider}` }}>
          {[
            ["panel", ap.tabPanel],
            ["permissions", ap.tabPermissions],
          ].map(([id, label]) => (
            <button key={id} onClick={() => { setActiveTab(id); localStorage.setItem("tab_adminpanel", id); }} style={{
              padding: "10px 20px", background: "none", border: "none", cursor: "pointer",
              color: activeTab === id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600,
              borderBottom: `2px solid ${activeTab === id ? t.accent.blue : "transparent"}`,
              transition: "all 0.2s",
            }}>{label}</button>
          ))}
        </div>

        {/* Widget Yetkileri Sekmesi */}
        {activeTab === "permissions" && <ContentPermissionsPanel t={t} ap={ap} />}

        {/* Panel Sekmesi */}
        {activeTab === "panel" && (
          <>
            {editMode && (
              <div style={{ padding: "14px 20px", marginBottom: 20, background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, borderRadius: 12, fontSize: 13, color: t.accent.yellow }}>
                <div style={{ fontWeight: 700, marginBottom: 10 }}>📌 {ap.editModeHint}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {Object.entries(WIDGET_DEFS).map(([id, def]) => (
                    <button key={id} onClick={() => toggleWidget(id)} style={{
                      padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
                      border: `1px solid ${hiddenWidgets.includes(id) ? t.border.input : t.accent.blue}`,
                      background: hiddenWidgets.includes(id) ? t.bg.toggle : t.accent.blueBg,
                      color: hiddenWidgets.includes(id) ? t.text.muted : t.accent.blue,
                      textDecoration: hiddenWidgets.includes(id) ? "line-through" : "none",
                    }}>{ap.widgetTitles[def.titleKey]}</button>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
                <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
                <span style={{ color: t.text.muted }}>{ap.panelLoading}</span>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {visibleLayout.map(id => {
                  const def = WIDGET_DEFS[id];
                  if (!def) return null;
                  const isWide = def.w === 2;
                  const isDraggingThis = dragging === id;
                  const isDragOverThis = dragOver === id;
                  return (
                    <div key={id}
                      className={`widget-card${isDragOverThis ? " widget-drag-over" : ""}`}
                      draggable={editMode}
                      onDragStart={() => handleDragStart(id)}
                      onDragOver={e => handleDragOver(e, id)}
                      onDrop={e => handleDrop(e, id)}
                      onDragEnd={handleDragEnd}
                      style={{
                        gridColumn: isWide ? "1 / -1" : "auto",
                        background: t.bg.card,
                        border: `1px solid ${isDraggingThis ? t.accent.blue : t.border.card}`,
                        borderRadius: 16, padding: "16px 20px",
                        boxShadow: isDraggingThis ? `0 12px 40px rgba(59,130,246,0.2)` : t.shadow.card,
                        opacity: isDraggingThis ? 0.5 : 1,
                        cursor: editMode ? "grab" : "default",
                        display: "flex", flexDirection: "column",
                        minHeight: isWide ? 260 : 240,
                        transition: "all 0.2s",
                        position: "relative", overflow: "hidden",
                      }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary }}>{ap.widgetTitles[def.titleKey]}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: "50%", background: t.accent.green, animation: "sf-pulse 2s ease-in-out infinite" }} />
                          {editMode && <span style={{ fontSize: 16, color: t.text.muted, cursor: "grab" }}>⠿</span>}
                        </div>
                      </div>
                      <div style={{ flex: 1, minHeight: 0 }}>{renderWidget(id, data, t, themeName, ap)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
